import {
  ScrollFade,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui'
import type { Check } from '@/modules/projects/stores'
import { SortableHead, useSort } from './sort'

interface DiscoveredAccount {
  accountNumber: string | null
  owner?: string
  institution?: string
  count: number
}

const headers: Array<[string, string, string?]> = [
  ['Account Number', 'accountNumber'],
  ['Account Owner', 'owner'],
  ['Institution', 'institution'],
  ['Checks', 'count', 'text-right'],
]

// A drawer account can show slightly different owner/institution spellings
// across its checks (OCR variance), so the most frequent non-empty value wins.
const mostCommon = (values: Array<string | null | undefined>) => {
  const counts = new Map<string, number>()
  for (const raw of values) {
    const value = raw?.trim()
    if (!value) continue
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }

  let best: string | undefined
  let bestCount = 0
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value
      bestCount = count
    }
  }
  return best
}

// One row per distinct drawer account printed on the checks. Checks whose
// account couldn't be read are grouped under a single "Unknown" row.
const groupByAccount = (checks: Check[]): DiscoveredAccount[] => {
  const groups = new Map<string, Check[]>()
  for (const check of checks) {
    const key = check.accountNumber?.trim() || ''
    const existing = groups.get(key)
    if (existing) existing.push(check)
    else groups.set(key, [check])
  }

  return Array.from(groups.entries()).map(([key, groupChecks]) => ({
    accountNumber: key || null,
    owner: mostCommon(groupChecks.map((check) => check.accountOwner)),
    institution: mostCommon(groupChecks.map((check) => check.institution)),
    count: groupChecks.length,
  }))
}

export const CheckAccountsSummary = ({ checks }: { checks: Check[] }) => {
  const { sort, setSort, sorter } = useSort({
    key: 'count',
    direction: 'desc',
  })

  if (!checks.length) return null

  const accounts = groupByAccount(checks).sort(sorter)

  return (
    <ScrollFade
      containerClassName="w-full rounded-lg border bg-white"
      className="max-h-[400px]"
    >
      <table className="w-full caption-bottom text-sm">
        <TableHeader className="sticky top-0 z-10 bg-blue-1">
          <TableRow className="bg-blue-1">
            {headers.map(([label, sortKey, className]) => (
              <SortableHead
                key={sortKey}
                label={label}
                sortKey={sortKey}
                sort={sort}
                onClick={setSort}
                className={className}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow key={account.accountNumber ?? 'unknown'}>
              <TableCell className="text-blue-6">
                {account.accountNumber ?? 'Unknown'}
              </TableCell>
              <TableCell className="text-blue-6">
                <div className="max-w-[200px] truncate">
                  {account.owner || '--'}
                </div>
              </TableCell>
              <TableCell className="text-blue-6">
                <div className="max-w-[200px] truncate">
                  {account.institution || '--'}
                </div>
              </TableCell>
              <TableCell className="text-right text-blue-6">
                {account.count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </table>
    </ScrollFade>
  )
}
